import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { NativeSelect } from './ui/native-select';
import {
  Database, Clock, Filter, RefreshCw, Award, FileText,
  TrendingUp, Users, ChevronDown, ChevronUp, Gamepad2
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { usePersistedState } from '../hooks/usePersistedState';

const VendasTab = () => {
  const hoje = new Date().toISOString().split('T')[0];

  // Estado principal
  const [fechos, setFechos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seccoesDisponiveis, setSeccoesDisponiveis] = useState([]);

  // Raspadinhas
  const [raspadinhas, setRaspadinhas] = useState([]);
  const [resumoRasp, setResumoRasp] = useState(null);
  const [showRasp, setShowRasp] = useState(false);

  // Filtros — validar datas na inicialização
  const [rawDataInicio, setDataInicio] = usePersistedState('vr_dt_ini', hoje);
  const [rawDataFim, setDataFim] = usePersistedState('vr_dt_fim', hoje);
  const [filtroSeccao, setFiltroSeccao] = usePersistedState('vr_sec', '');
  const [filtroColab, setFiltroColab] = usePersistedState('vr_col', '');
  const [filtroTurno, setFiltroTurno] = usePersistedState('vr_tur', '');

  // Garantir dataFim >= dataInicio sempre
  const dataInicio = rawDataInicio || hoje;
  const dataFim = (rawDataFim && rawDataFim >= dataInicio) ? rawDataFim : dataInicio;

  // Request counter para descartar respostas de requests antigos
  const requestIdRef = useRef(0);

  const carregarFechos = useCallback(async () => {
    const thisRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('data_inicio', dataInicio);
      params.append('data_fim', dataFim);
      if (filtroSeccao) params.append('seccao', filtroSeccao);
      if (filtroColab) params.append('colaborador', filtroColab);
      if (filtroTurno) params.append('turno', filtroTurno);

      const res = await api.get(`/vendas-registo/fechos-turno?${params.toString()}`);

      // Só aceitar se for o request mais recente
      if (thisRequestId !== requestIdRef.current) return;

      setFechos(res.data.fechos || []);
      setResumo(res.data.resumo || null);
      setSeccoesDisponiveis(res.data.seccoes_disponiveis || []);
    } catch (err) {
      if (thisRequestId !== requestIdRef.current) return;
      console.error('Erro ao carregar fechos:', err);
      toast.error('Erro ao consultar vendas XD');
    } finally {
      if (thisRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [dataInicio, dataFim, filtroSeccao, filtroColab, filtroTurno]);

  const carregarRaspadinhas = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('data_inicio', dataInicio);
      if (dataFim) params.append('data_fim', dataFim);
      if (filtroColab) params.append('colaborador', filtroColab);

      const res = await api.get(`/vendas-registo/raspadinhas?${params.toString()}`);
      setRaspadinhas(res.data.registos || []);
      setResumoRasp(res.data.resumo || null);
    } catch (err) {
      console.error('Erro ao carregar raspadinhas:', err);
    }
  }, [dataInicio, dataFim, filtroColab]);

  useEffect(() => {
    carregarFechos();
    carregarRaspadinhas();
  }, [carregarFechos, carregarRaspadinhas]);

  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const turnoLabel = (t) => {
    if (t === 'manha') return 'Manha';
    if (t === 'tarde') return 'Tarde';
    return t || '—';
  };

  const turnoColor = (t) => {
    if (t === 'manha') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (t === 'tarde') return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    return 'bg-gray-50 text-gray-500 border-gray-200';
  };

  return (
    <div className="space-y-4" data-testid="vendas-registo-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Vendas (XD)</h2>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
              <Database className="h-3 w-3 mr-1" /> XD Live
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm mt-0.5">
            Vendas por turno e colaborador — fonte XD
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { carregarFechos(); carregarRaspadinhas(); }}
          disabled={loading}
          data-testid="vendas-registo-btn-refresh"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card data-testid="vendas-registo-filtros">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[120px]">
              <Label className="text-xs text-muted-foreground mb-1 block">De</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => {
                  const novaData = e.target.value;
                  setDataInicio(novaData);
                  if (novaData > dataFim) setDataFim(novaData);
                }}
                className="h-8 text-sm"
                data-testid="vendas-registo-dt-inicio"
              />
            </div>
            <div className="flex-1 min-w-[120px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Ate</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => {
                  const novaData = e.target.value;
                  setDataFim(novaData);
                  if (novaData < dataInicio) setDataInicio(novaData);
                }}
                className="h-8 text-sm"
                data-testid="vendas-registo-dt-fim"
              />
            </div>
            <div className="flex-1 min-w-[120px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Seccao</Label>
              <NativeSelect
                value={filtroSeccao}
                onChange={(e) => setFiltroSeccao(e.target.value)}
                className="h-8 text-sm"
                data-testid="vendas-registo-seccao"
              >
                <option value="">Todas</option>
                {seccoesDisponiveis.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </NativeSelect>
            </div>
            <div className="flex-1 min-w-[120px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Colaborador</Label>
              <Input
                type="text"
                placeholder="Nome..."
                value={filtroColab}
                onChange={(e) => setFiltroColab(e.target.value)}
                className="h-8 text-sm"
                data-testid="vendas-registo-colab"
              />
            </div>
            <div className="flex-1 min-w-[100px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Turno</Label>
              <NativeSelect
                value={filtroTurno}
                onChange={(e) => setFiltroTurno(e.target.value)}
                className="h-8 text-sm"
                data-testid="vendas-registo-turno"
              >
                <option value="">Todos</option>
                <option value="manha">Manha</option>
                <option value="tarde">Tarde</option>
              </NativeSelect>
            </div>
            <div>
              {(filtroSeccao || filtroColab || filtroTurno || dataInicio !== hoje || dataFim !== hoje) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setDataInicio(hoje);
                    setDataFim(hoje);
                    setFiltroSeccao('');
                    setFiltroColab('');
                    setFiltroTurno('');
                  }}
                  data-testid="vendas-registo-limpar"
                >
                  <Filter className="h-3 w-3 mr-1" /> Limpar
                </Button>
              )}
            </div>
            <div>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={async () => {
                  const ano = parseInt(dataInicio.slice(0, 4));
                  const mes = parseInt(dataInicio.slice(5, 7));
                  if (!window.confirm(
                    `Recalcular pontos das Raspadinhas de ${String(mes).padStart(2, '0')}/${ano}?\n\n` +
                    `Vai actualizar os pontos_ganhos de todos os fechos deste mês ` +
                    `com base nas regras de pontos actuais. Depois ficam fossilizados.`
                  )) return;
                  try {
                    const params = new URLSearchParams({
                      seccao: 'Raspadinhas', ano, mes, dry_run: 'false'
                    });
                    const res = await api.post(`/admin/pontos/recalcular?${params.toString()}`);
                    toast.success(
                      `${res.data.total_recalculadas} fecho(s) recalculado(s). ` +
                      `Recarregando...`
                    );
                    // Recarregar KPIs
                    setTimeout(() => window.location.reload(), 1200);
                  } catch (e) {
                    toast.error('Erro: ' + (e?.response?.data?.detail || e.message));
                  }
                }}
                data-testid="vendas-recalcular-pontos-mes"
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Recalcular Pontos Mês
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards — inclui Raspadinhas quando disponíveis */}
      {resumo && (() => {
        const raspVendas = resumoRasp?.total_vendas || 0;
        const raspPontos = resumoRasp?.total_pontos || 0;
        const raspRegistos = resumoRasp?.total_registos || 0;
        const totalVendasCombinado = (resumo.total_vendas || 0) + raspVendas;
        const totalPontosCombinado = (resumo.total_pontos || 0) + raspPontos;
        return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vendas-registo-kpis">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-blue-500 font-medium">Total Vendas</p>
                  <p className="text-lg font-bold text-blue-700" data-testid="kpi-total-vendas">{fmt(totalVendasCombinado)}</p>
                  {raspVendas > 0 && (
                    <p className="text-[9px] text-blue-500/70">
                      XD {fmt(resumo.total_vendas)} + Rasp {fmt(raspVendas)}
                    </p>
                  )}
                </div>
                <TrendingUp className="h-5 w-5 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-500 font-medium">Total Pontos</p>
                  <p className="text-lg font-bold text-emerald-700" data-testid="kpi-total-pontos">{totalPontosCombinado}</p>
                  {raspPontos > 0 && (
                    <p className="text-[9px] text-emerald-500/70">
                      XD {resumo.total_pontos} + Rasp {raspPontos}
                    </p>
                  )}
                </div>
                <Award className="h-5 w-5 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-violet-50 to-white border-violet-100">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-violet-500 font-medium">Turnos / Registos</p>
                  <p className="text-lg font-bold text-violet-700" data-testid="kpi-total-turnos">{(resumo.total_turnos || 0) + raspRegistos}</p>
                  {raspRegistos > 0 && (
                    <p className="text-[9px] text-violet-500/70">
                      XD {resumo.total_turnos} + Rasp {raspRegistos}
                    </p>
                  )}
                </div>
                <Clock className="h-5 w-5 text-violet-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-amber-500 font-medium">Turnos c/ Pontos</p>
                  <p className="text-lg font-bold text-amber-700" data-testid="kpi-turnos-pontos">
                    {resumo.turnos_com_pontos}/{resumo.total_turnos}
                  </p>
                </div>
                <Users className="h-5 w-5 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>
        );
      })()}

      {/* Tabela de Vendas por Turno */}
      <Card data-testid="vendas-registo-tabela-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-500" />
            Vendas por Turno — XD
            {!loading && (
              <span className="text-xs font-normal text-muted-foreground">
                ({fechos.length} registos{resumo && ` | ${dataInicio === dataFim ? new Date(dataInicio + 'T00:00:00').toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long' }) : `${dataInicio} a ${dataFim}`}`})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-400" />
              <span className="text-sm text-muted-foreground">A consultar XD...</span>
            </div>
          ) : fechos.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Database className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sem fechos de turno para o periodo selecionado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vendas-registo-tabela">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="h-9 px-3 text-left text-xs font-medium text-muted-foreground">Data</th>
                    <th className="h-9 px-3 text-left text-xs font-medium text-muted-foreground">Seccao</th>
                    <th className="h-9 px-3 text-left text-xs font-medium text-muted-foreground">Colaborador</th>
                    <th className="h-9 px-3 text-left text-xs font-medium text-muted-foreground">Turno</th>
                    <th className="h-9 px-3 text-right text-xs font-medium text-muted-foreground">Vendas</th>
                    <th className="h-9 px-3 text-center text-xs font-medium text-muted-foreground">Docs</th>
                    <th className="h-9 px-3 text-center text-xs font-medium text-muted-foreground">Origem</th>
                    <th className="h-9 px-3 text-center text-xs font-medium text-muted-foreground">Pontos</th>
                    <th className="h-9 px-3 text-center text-xs font-medium text-muted-foreground">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {fechos.map((f, idx) => (
                    <tr
                      key={`${f.data}-${f.colaborador_id}-${f.turno}-${idx}`}
                      className="border-b hover:bg-muted/20 transition-colors"
                      data-testid={`fecho-row-${idx}`}
                    >
                      <td className="px-3 py-2 font-medium text-xs">
                        {new Date(f.data + 'T00:00:00').toLocaleDateString('pt-PT', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px] font-medium">{f.seccao}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs font-medium uppercase tracking-wide">{f.colaborador_nome}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${turnoColor(f.turno)}`}>
                          {turnoLabel(f.turno)}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground ml-1">{f.entrada}-{f.saida}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-xs">
                        {fmt(f.valor_vendas)}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-muted-foreground">{f.num_documentos}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200">
                          {f.origem}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {f.pontos > 0 ? (
                          <Badge className="bg-emerald-600 text-white text-[10px]">{f.pontos} pts</Badge>
                        ) : f.elegivel_pontos ? (
                          <span className="text-[10px] text-amber-500">0 pts</span>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge
                          variant="outline"
                          className={`text-[9px] ${
                            f.estado === 'validado'
                              ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                              : f.estado === 'pendente_fora_horario'
                              ? 'bg-orange-50 text-orange-600 border-orange-200'
                              : f.estado === 'apoio'
                              ? 'bg-blue-50 text-blue-600 border-blue-200'
                              : 'bg-amber-50 text-amber-600 border-amber-200'
                          }`}
                          title={
                            f.estado === 'pendente_fora_horario'
                              ? 'Vendas fora de qualquer turno — não atribuídas para pontos'
                              : f.estado === 'validado'
                              ? 'Validado — vendas dentro do turno real'
                              : f.estado === 'apoio'
                              ? 'Colaborador de apoio — outro colaborador com login XD cobriu este turno'
                              : 'XD indisponível ou sem turno'
                          }
                        >
                          {f.estado === 'validado'
                            ? 'Validado'
                            : f.estado === 'pendente_fora_horario'
                            ? 'Fora horário'
                            : f.estado === 'apoio'
                            ? 'Apoio'
                            : 'Pendente'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raspadinhas / Jogos — Zona Manual Separada */}
      <Card data-testid="vendas-registo-raspadinhas">
        <CardHeader
          className="pb-2 cursor-pointer select-none"
          onClick={() => { setShowRasp(!showRasp); if (!showRasp) carregarRaspadinhas(); }}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Gamepad2 className="h-4 w-4 text-orange-500" />
              Raspadinhas / Jogos
              <Badge variant="outline" className="text-[9px] bg-orange-50 text-orange-600 border-orange-200">Manual</Badge>
              {resumoRasp && (
                <span className="text-xs font-normal text-muted-foreground">
                  ({resumoRasp.total_registos} registos, {fmt(resumoRasp.total_vendas)})
                </span>
              )}
            </CardTitle>
            {showRasp ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </CardHeader>
        {showRasp && (
          <CardContent className="pt-0">
            {raspadinhas.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">Sem registos de raspadinhas/jogos para o periodo.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="h-8 px-3 text-left text-xs font-medium text-muted-foreground">Data</th>
                      <th className="h-8 px-3 text-left text-xs font-medium text-muted-foreground">Colaborador</th>
                      <th className="h-8 px-3 text-left text-xs font-medium text-muted-foreground">Seccao</th>
                      <th className="h-8 px-3 text-right text-xs font-medium text-muted-foreground">Valor</th>
                      <th className="h-8 px-3 text-center text-xs font-medium text-muted-foreground">Pontos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {raspadinhas.map((r, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/20">
                        <td className="px-3 py-1.5 text-xs">{r.data ? new Date(r.data + 'T00:00:00').toLocaleDateString('pt-PT') : '—'}</td>
                        <td className="px-3 py-1.5 text-xs font-medium uppercase">{r.colaborador_nome || '—'}</td>
                        <td className="px-3 py-1.5">
                          <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-600 border-orange-200">{r.seccao}</Badge>
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs font-semibold">{fmt(r.valor)}</td>
                        <td className="px-3 py-1.5 text-center">
                          {r.pontos_ganhos > 0 ? (
                            <Badge className="bg-emerald-600 text-white text-[10px]">{r.pontos_ganhos} pts</Badge>
                          ) : (
                            <span className="text-[10px] text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default VendasTab;
